import { Spinner } from "@npco/zeller-design-system";
import * as Illustration from "@npco/zeller-design-system/illustration";

import { PageTemplate } from "./page-template";

/**
 * The pages every terminal operation shows, so purchase and both refund flows
 * declare them once. Pages belonging to a single operation — its outcome
 * screens, transaction lookup, tip entry — stay in that flow's own render.
 *
 * `operation` names the thing being performed, for the handful of pages whose
 * copy is otherwise identical.
 */
export function createOperationPages(operation: "Payment" | "Refund") {
	return {
		Initialising: PageTemplate({
			image: <Spinner size="lg" />,
			title: "Connecting",
			intermediate: true,
		}),
		AwaitProcessing: PageTemplate({
			image: <Spinner size="lg" />,
			title: `Processing ${operation}`,
			intermediate: true,
		}),
		AwaitAccountSelection: PageTemplate({
			image: <Illustration.TerminalT2Application />,
			title: "Waiting for Account selection",
			intermediate: true,
		}),
		AwaitApplicationSelection: PageTemplate({
			image: <Illustration.TerminalT2Application />,
			title: "Waiting for Card Application selection",
			intermediate: true,
		}),
		AwaitPaymentMethod: PageTemplate({
			image: <Illustration.TerminalT2TapNow />,
			title: "Waiting for Card or Device to be presented",
			intermediate: true,
		}),
		AwaitPinEntry: PageTemplate({
			image: <Illustration.TerminalT2Pin />,
			title: "Waiting for Card PIN entry",
			intermediate: true,
		}),
		AwaitSignature: PageTemplate({
			image: <Illustration.TerminalT2Signature />,
			title: "Waiting for Signature entry",
			intermediate: true,
		}),
		DisplayPaymentMessage: PageTemplate({
			image: <Illustration.TerminalT2Application />,
			title: "Card Payment Message pending",
			intermediate: true,
		}),
		Cancelled: PageTemplate({
			image: <Illustration.SuccessThumbsUp />,
			title: `${operation} Cancelled`,
		}),
		ErrorDisconnected: PageTemplate({
			image: <Illustration.Error />,
			title: "Terminal Error",
			message:
				"The Terminal has lost connection with this POS register. Please check the connection and try again. If this is the first time you're setting up the register, you may need to set up the terminal connection first.",
		}),
		ErrorInactivityTimeout: PageTemplate({
			image: <Illustration.ErrorBoatSinking loop={false} />,
			title: "Cancelled due to inactivity",
			message: `${operation} has been cancelled due to inactivity. Please try again.`,
		}),
		ErrorNetwork: PageTemplate({
			image: <Illustration.ErrorBoatSinking loop={false} />,
			title: "Unable to connect to Zeller",
			message: "Please ensure this device is connected to the internet.",
			steps: [
				"Confirm your device is connected to your network.",
				"Check if the network is connected to the internet.",
			],
		}),
		ErrorOperation: PageTemplate({
			image: <Illustration.Error />,
			title: "Operation Failed",
			// "transaction" rather than the operation: this page also covers
			// protocol failures, where nothing has been processed yet.
			message:
				"An unrecoverable error occurred when processing this transaction. Please try again.",
		}),
		ErrorTerminalBusy: PageTemplate({
			image: <Illustration.ErrorDominoes loop={false} />,
			title: "Terminal Busy",
			message:
				"The terminal is currently busy. Please check the Terminal and try again.",
		}),
		ErrorTerminalUnreachable: PageTemplate({
			image: <Illustration.ErrorDominoes loop={false} />,
			title: "Failed to connect to Terminal",
			message:
				"Please check that your Zeller Terminal is connected to the Internet and try again.",
		}),
		ErrorTerminalMissing: PageTemplate({
			image: <Illustration.ErrorQuestionMark />,
			title: "No paired terminal found",
			message: `This device is not connected to a Zeller Terminal. Please pair it to process a ${operation.toLowerCase()}.`,
		}),
	};
}
